import type { AudioFilePort, AudioFileProbe } from '@Domain/Ports/AudioFile/AudioFilePort';

/** The slice of `HTMLAudioElement` this adapter needs to read duration metadata off a local file. */
type AudioElementLike = {
	src: string;
	readonly duration: number;
	addEventListener(type: 'loadedmetadata' | 'error', listener: () => void): void;
	removeEventListener(type: 'loadedmetadata' | 'error', listener: () => void): void;
};

type AudioElementFactory = () => AudioElementLike;
type ObjectUrlFactory = (file: Blob) => string;
type ObjectUrlRevoker = (objectUrl: string) => void;

/**
 * Probes a local audio `File` by loading it into an `<audio>` element and reading its metadata,
 * without decoding a single frame.
 *
 * Every browser surface it touches — the `<audio>` element factory, `URL.createObjectURL` and
 * `URL.revokeObjectURL` — is received by the constructor rather than read from a global inside
 * `probe()`, with a default resolved at construction time, never captured at module load. jsdom
 * never fires `loadedmetadata`, so exercising this adapter under test means constructing it with a
 * fake factory that does; the composition root can still build it unconditionally, since building
 * it opens nothing.
 *
 * The object URL created for a probe is revoked in every case — on `'readable'` and on
 * `'unreadable'` alike — because the probed files run to tens of megabytes and a un-revoked object
 * URL keeps its `Blob` alive in memory for the life of the document. `probe()` itself never
 * rejects: an unreadable file is a reported outcome, not a thrown error.
 */
class WebAudioFileAdapter implements AudioFilePort {
	constructor(
		private readonly createAudioElement: AudioElementFactory = () => document.createElement('audio'),
		private readonly createObjectUrl: ObjectUrlFactory = (file) => URL.createObjectURL(file),
		private readonly revokeObjectUrl: ObjectUrlRevoker = (objectUrl) => URL.revokeObjectURL(objectUrl),
	) {}

	probe(file: File): Promise<AudioFileProbe> {
		return new Promise<AudioFileProbe>((resolve) => {
			const objectUrl = this.createObjectUrl(file);
			const audioElement = this.createAudioElement();

			const cleanup = (): void => {
				audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
				audioElement.removeEventListener('error', handleError);
				this.revokeObjectUrl(objectUrl);
			};

			const handleLoadedMetadata = (): void => {
				cleanup();
				const { duration } = audioElement;
				// A duration the platform cannot report (unknown-length stream) surfaces as
				// `NaN` or `Infinity`: this is still a readable file, just one with no known length.
				resolve({
					outcome: 'readable',
					durationMs: Number.isFinite(duration) ? Math.round(duration * 1000) : null,
				});
			};

			const handleError = (): void => {
				cleanup();
				resolve({ outcome: 'unreadable' });
			};

			audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
			audioElement.addEventListener('error', handleError);
			audioElement.src = objectUrl;
		});
	}
}

export default WebAudioFileAdapter;
export type { AudioElementLike };
